import { useCallback, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { EnvelopeIcon } from '@heroicons/react/24/outline'

import { Button } from '@elements/button'
import { ErrorNote } from '@elements/error-note'
import { TextField } from '@components/form-field/text'
import { TimeField } from '@components/form-field/time'
import { CheckboxField } from '@components/form-field/checkbox'
import { DateTextField } from '@components/form-field/date-text'
import { PhoneField } from '@components/form-field/phone'

import { bookingApi } from '../data'
import { createValidator, validations } from '@lib/validation'
import { countryCodeSafePhone, getId } from '@utils/strings'
import {
  getDateInputPlaceholderByVenueDateDisplayFormat,
  getLuxonDateFormatByVenueDateDisplayFormat,
  isoToUTC,
  utcRange,
  utcToISO,
  withDateFormat,
} from '@utils/date-time'
import { Venue } from 'types/model'
import { AdmissionGroup } from 'types/model/admission-group'
import { useSession } from '@hooks/session'
import { TrackEvent } from '@utils/analytics/types'
import { analytics } from '@lib/analytics'
import { trackEvent } from '@lib/track'
import { TrackingEvent } from 'shared'

export interface ReservationFormProps {
  venue: Venue
  date: string
  afterSubmit: (vals: AdmissionGroup) => void
  attributionId?: string | null
  affiliateId?: string | null
}

type ReservationFormValues = Partial<{
  name: string
  email: string
  phone: string
  birthday: string
  guestsExpected: number
  arrivalTime: number
  notes: string | null
  customerMarketingOptIn: boolean
}>

const reservationValidator = createValidator({
  name: validations.string().required('Please provide your full name'),
  email: validations
    .string()
    .required('Please provide your email address')
    .email(),
  phone: validations
    .string()
    .required('Please provide your phone number')
    .min(10, 'Please provide a full phone number'),
  birthday: validations.string().required('Please provide your birthday'),
  arrivalTime: validations.number().required('Please select a time'),
  guestsExpected: validations
    .number()
    .required('Please provide the number of guests (including you)')
    .min(1),
  notes: validations.string().nullable(),
})

export function resolveReservationArrivalTimeMs(
  arrivalTime: number | null | undefined
): number | undefined {
  if (arrivalTime == null) return undefined
  const parsed = Number(arrivalTime)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function ReservationForm({
  venue,
  date,
  afterSubmit,
  attributionId,
  affiliateId,
}: ReservationFormProps) {
  const session = useSession()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [currentDate, setCurrentDate] = useState(date)
  const venueTimezone = venue.timezone || venue.location?.timezone
  const venueDateFormat = getLuxonDateFormatByVenueDateDisplayFormat(
    venue.dateDisplayFormat
  )
  const dateInputPlaceholder = getDateInputPlaceholderByVenueDateDisplayFormat(
    venue.dateDisplayFormat
  )

  const initialValues = useMemo(
    () => ({
      name: session.user ? session.user.name : '',
      email: session.user ? session.user.email : '',
      phone: session.user ? countryCodeSafePhone(session.user.phone) : '',
      birthday: session.user?.birthday
        ? new Date(session.user.birthday).toISOString()
        : undefined,
      customerMarketingOptIn: session.user
        ? session.user.marketingOptIn
        : false,
    }),
    [session.user]
  )

  const submit = useCallback(
    async (vals: ReservationFormValues) => {
      try {
        if (!venue) throw new Error('')
        const {
          arrivalTime,
          name,
          email,
          phone,
          birthday,
          customerMarketingOptIn,
          guestsExpected,
          notes = null,
        } = vals

        const birthDt = new Date(birthday as string)
        const birthdayDay = birthDt.getDate()
        const birthdayMonth = birthDt.getMonth()
        const birthdayYear = birthDt.getFullYear()

        const arrivalTimeTrueUtc = resolveReservationArrivalTimeMs(arrivalTime)

        const response = await bookingApi.requestReservation({
          affiliateId,
          attributionId,
          arrivalTime: arrivalTimeTrueUtc ? arrivalTimeTrueUtc : undefined,
          birthday: withDateFormat(birthDt, venueDateFormat),
          birthdayDay,
          birthdayMonth,
          birthdayYear,
          customerMarketingOptIn,
          date: isoToUTC(date),
          email,
          guestsExpected: Number(guestsExpected),
          guestsExpectedUnisex: Number(guestsExpected),
          guestsExpectedMale: null,
          guestsExpectedFemale: null,
          name,
          notes,
          phone,
          type: 'TABLE',
          venueId: getId(venue),
        })

        analytics.trackEvent({
          event: TrackEvent.ReservationRequested,
          venue,
          eventData: {
            date: arrivalTimeTrueUtc
              ? utcToISO(arrivalTimeTrueUtc)
              : undefined,
            dateFormatted: arrivalTimeTrueUtc
              ? withDateFormat(
                  arrivalTimeTrueUtc,
                  venueDateFormat,
                  venueTimezone
                )
              : undefined,
            arrivalTime: arrivalTimeTrueUtc
              ? withDateFormat(arrivalTimeTrueUtc, 'h:mm a', venueTimezone)
              : undefined,
            guestCount: guestsExpected,
            type: 'TABLE',
            items: [
              {
                itemName: response.inventoryName,
                itemId: response.id,
                itemPrice: response.inventoryPrice,
                itemCategory: response.inventoryType,
                itemQuantity: 1,
              },
            ],
            currency: venue.currencyCode,
            total: response.inventoryPrice,
            purchaseId: response.id,
          },
        })
        void trackEvent({
          event: TrackingEvent.ReservationRequested,
          venueId: venue.id,
          admissionGroupId: response.id,
        })

        setErrorMessage(undefined)
        afterSubmit?.(response)
      } catch (e) {
        console.error('ERROR', e)

        if (e instanceof Error) setErrorMessage(e.message)
      }
    },
    [
      affiliateId,
      afterSubmit,
      attributionId,
      date,
      venue,
      venueDateFormat,
      venueTimezone,
    ]
  )

  const [arrivalStart, arrivalEnd] = useMemo(() => {
    const [dateStart, dateEnd] = utcRange(currentDate, { days: 1 })
    return [dateStart, dateEnd - 60_000 * 15]
  }, [currentDate])

  return (
    <Form
      onSubmit={submit}
      initialValues={initialValues}
      validate={reservationValidator}
    >
      {({ handleSubmit, submitting, form }) => {
        if (date !== currentDate) {
          setCurrentDate(date)
          form.change('arrivalTime', undefined)
        }

        return (
          <form onSubmit={handleSubmit} className='contents'>
            <div className='space-y-6'>
              <div className='space-y-3'>
                <TextField
                  name='name'
                  label='Full Name'
                  placeholder='Please enter your name'
                  autoComplete='name'
                />
                <TextField
                  name='email'
                  label='Email'
                  placeholder='johndoe@email.com'
                  type='email'
                  autoComplete='email'
                />
                <div className='flex flex-col items-start justify-center w-full space-y-3 xs:space-y-0 xs:space-x-3 xs:flex-row'>
                  <PhoneField name='phone' label='Phone' />
                  <DateTextField
                    name='birthday'
                    label='Birthday'
                    initialValue={initialValues.birthday}
                    placeholder={dateInputPlaceholder}
                  />
                </div>
                <div className='flex flex-col items-start justify-center w-full space-y-3 xs:space-y-0 xs:space-x-3 xs:flex-row'>
                  <TextField
                    type='tel'
                    label='Guest Count'
                    name='guestsExpected'
                    placeholder='# of guests arriving'
                  />
                  <TimeField
                    label='Reservation Time'
                    name='arrivalTime'
                    startOption={arrivalStart}
                    endOption={arrivalEnd}
                    displayTimezone={venueTimezone}
                  />
                </div>
                <TextField
                  as='textarea'
                  name='notes'
                  label='Questions and Comments'
                  placeholder='Anything additional here'
                />
                {venue?.showMarketingOptIn && (
                  <CheckboxField name='customerMarketingOptIn'>
                    <span className='text-base sm:text-md'>
                      {venue.marketingOptInText ??
                        `Yes I'd like to receive messages and notifications from ${venue.name}`}
                    </span>
                  </CheckboxField>
                )}
              </div>
              <Button.Submit
                rightIcon={EnvelopeIcon}
                className='w-full'
                loading={submitting}
              >
                Submit Request
              </Button.Submit>
              <ErrorNote>{errorMessage}</ErrorNote>
            </div>
          </form>
        )
      }}
    </Form>
  )
}
